import { Link, createFileRoute } from "@tanstack/react-router";
import { BookOpen, Headphones, Search } from "lucide-react";
import { useMemo, useState } from "react";

import { EpisodePlatformLinks } from "#/components/PlatformLinks";
import { listEpisodes } from "#/lib/bookling";
import { gdiyEpisodeUrl } from "#/lib/gdiy";
import { includesQuery, normalizedQuery } from "#/lib/search";
import { SITE_NAME, absoluteUrl, canonicalLink, jsonLdScript, seoMeta } from "#/lib/seo";

import type { EpisodeView } from "#/lib/bookling-data";

export const Route = createFileRoute("/episodes")({
  loader: () => listEpisodes(),
  head: ({ loaderData }) => {
    const episodes = loaderData ?? [];
    const description = "All GDIY episodes with the books mentioned in each.";

    return {
      meta: seoMeta({
        title: `Episodes | ${SITE_NAME}`,
        description,
        path: "/episodes",
      }),
      links: canonicalLink("/episodes"),
      scripts: [
        jsonLdScript({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "GDIY episodes indexed by Bookling",
          description,
          url: absoluteUrl("/episodes"),
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: episodes.length,
            itemListElement: episodes.slice(0, 50).map((episode, index) => ({
              "@type": "ListItem",
              position: index + 1,
              item: {
                "@type": "PodcastEpisode",
                name: episode.title,
                episodeNumber: episode.number,
                url: gdiyEpisodeUrl(episode.guest) ?? undefined,
                partOfSeries: {
                  "@type": "PodcastSeries",
                  name: "Generation Do It Yourself",
                  url: "https://www.gdiy.fr/",
                },
              },
            })),
          },
        }),
      ],
    };
  },
  component: EpisodesPage,
});

function EpisodesPage() {
  const episodes = Route.useLoaderData();
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = normalizedQuery(query);
    if (!q) return episodes;
    return episodes.filter((ep) => {
      if (
        includesQuery(ep.title, q) ||
        includesQuery(ep.guest, q) ||
        includesQuery(ep.company, q)
      ) {
        return true;
      }
      return ep.books.some((b) => includesQuery(b.title, q) || includesQuery(b.author, q));
    });
  }, [episodes, query]);

  const totalBooks = episodes.reduce((acc, ep) => acc + ep.books.length, 0);

  return (
    <main>
      <section className="hero-band">
        <div className="page-wrap hero-layout">
          <div className="hero-copy">
            <p className="eyebrow">GDIY episodes</p>
            <h1>Every episode, with their books.</h1>
          </div>

          <div className="signal-board" aria-label="Episodes status">
            <div>
              <Headphones className="h-5 w-5" aria-hidden="true" />
              <span>{episodes.length} episodes</span>
            </div>
            <strong>{totalBooks}</strong>
            <p>book mentions across the archive.</p>
          </div>
        </div>
      </section>

      <section className="page-wrap library-section" aria-label="Episodes">
        <div className="library-toolbar">
          <label className="library-search">
            <Search className="h-4 w-4" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search episode, guest, company, book"
              aria-label="Search episodes"
            />
          </label>
          <p className="toolbar-count">{filtered.length} shown</p>
        </div>

        {filtered.length === 0 ? (
          <p className="library-empty">No episodes match that search.</p>
        ) : (
          <ul className="episode-list">
            {filtered.map((ep) => (
              <EpisodeRow key={ep.id} episode={ep} />
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}

function EpisodeRow({ episode }: { episode: EpisodeView }) {
  return (
    <li className="episode-row">
      <header className="episode-head">
        <div className="episode-head-main">
          <p className="episode-number">#{episode.number}</p>
          <h3>{episode.title}</h3>
          <p className="episode-guest">
            {episode.guest}
            {episode.company ? <span> · {episode.company}</span> : null}
          </p>
        </div>
        <EpisodePlatformLinks episode={episode} className="episode-links" />
      </header>

      {episode.books.length === 0 ? (
        <p className="episode-empty">
          <BookOpen className="h-4 w-4" aria-hidden="true" />
          No books indexed for this episode yet.
        </p>
      ) : (
        <ul className="episode-books">
          {episode.books.map((book) => (
            <li key={`${episode.id}-${book.id}`}>
              <Link
                to="/books/$slug"
                params={{ slug: book.slug }}
                className="episode-book"
                aria-label={`${book.title} by ${book.author}`}
              >
                {book.imageUrl ? (
                  <img src={book.imageUrl} alt="" className="episode-book-cover" loading="lazy" />
                ) : (
                  <div
                    className="episode-book-cover episode-book-cover--placeholder"
                    aria-hidden="true"
                  >
                    <span>{book.title.slice(0, 1)}</span>
                  </div>
                )}
                <div className="episode-book-body">
                  <p className="episode-book-title">{book.title}</p>
                  <p className="episode-book-author">{book.author}</p>
                  {book.timestamp ? (
                    <p className="episode-book-timestamp">{book.timestamp}</p>
                  ) : null}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
